import { Pagination as AntPagination } from 'antd';
import { observer } from 'mobx-react-lite';
import type { ComponentProps } from 'react';
import { CssColors } from 'src/styles/CssVariables';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import type { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import styled from 'styled-components';

type AntPaginationProps = ComponentProps<typeof AntPagination>;

/**
 * Props for the `Pagination` component.
 *
 * @property store - MobX `BasicStore` instance whose `pager` values
 * are used as the data source for the underlying Ant Design `Pagination`.
 * @property onChange - Optional handler that is called after the store pager
 * is updated and the list is reloaded.
 */
type PaginationProps = {
    store: BasicStore<Entity>;
    onChange?: AntPaginationProps['onChange'];
} & Omit<AntPaginationProps, 'total' | 'pageSize' | 'current' | 'onChange'>;

/**
 * Pagination component that binds Ant Design `Pagination` to a MobX `BasicStore` pager.
 * It automatically updates the pager state in the store and reloads the list
 * whenever the page or page size changes.
 *
 * Note: this component is not 100% generic. It assumes specific pager behavior
 * (e.g. index base and list reload strategy) used in this project, and may
 * require adjustments when reused in other projects.
 */
export const Pagination = observer(
    ({ store, onChange, ...rest }: PaginationProps) => {
        if (!store.pager) {
            return null;
        }

        const handleChange: AntPaginationProps['onChange'] = (
            page,
            pageSize,
        ) => {
            store.pager?.setPage(page - 1); // WARNING: Ant Design `Pagination` uses 1-based indexing, but our store uses 0-based indexing.
            store.pager?.setSize(pageSize);
            store.loadList(); // WARNING: This will reload the list every time the page or page size changes. This functionality is hardcoded;

            onChange?.(page, pageSize);
        };

        return (
            <StyledPagination
                total={store.pager?.total}
                pageSize={store.pager?.size}
                current={(store.pager?.page ?? 0) + 1} // WARNING: Ant Design `Pagination` uses 1-based indexing, but our store uses 0-based indexing.
                onChange={handleChange}
                {...rest}
            />
        );
    },
);

const StyledPagination = styled(AntPagination)`
    padding-left: 0;
    .ant-pagination-item:not(.ant-pagination-item-disabled) {
        &:hover {
            a {
                color: ${CssColors.black};
            }
        }

        a {
            color: ${CssColors.gray};
        }

        &.ant-pagination-item-active {
            a {
                color: ${CssColors.black};
            }
        }
    }

    .ant-pagination-prev:not(.ant-pagination-disabled):hover,
    .ant-pagination-next:not(.ant-pagination-disabled):hover {
        .ant-pagination-item-link {
            color: ${CssColors.black};
            border-color: ${CssColors.black};
        }
    }
`;
